import { useCallback, useMemo, useState, useEffect, useContext } from 'react';
import { defaultQueryClient } from '@shared/query-client/query-client';
import { MAIN_CONTENT_ID } from '@shared/ui/skip-link';
import { useNavigate } from '@tanstack/react-router';

import styles from './home.module.css';

import {
	Issue,
	ACCESS_TOKEN_KEY,
	ACCESS_TOKEN_EXPIRE_AT_KEY,
	FAVORITE_KEY,
	USER_ROLE,
} from '@/constants';
import config from '@/config';
import { TemplatePoets } from '@shared/config';
import { FavoritesDrawer } from '@widgets/favorites-drawer';
import { DownloadsDrawer } from '@widgets/downloads-drawer';
import { Topbar as TopbarWidget } from '@shared/ui/topbar';
import {
	AppMenu,
	AppMenuButton,
	type AppMenuTemplateItem,
} from '@shared/ui/app-menu';
import { useNavbarIconUrl } from '@shared/lib/hooks';

import {
	AUTH_KEY,
	AUTH_EXPIRATION_DATE_KEY,
} from '@shared/auth/authentication.hooks';
import {
	forgetCacheOwner,
	hasCacheOwnerChanged,
	rememberCacheOwner,
} from '@shared/auth';
import { AuthenticationView } from '@features/direct-login';
import { TopStories } from './stories/top-stories';
import { DailyNews } from './daily-news/daily-news';
import { Magazines } from './magazines/magazines';
import { Contest } from './contest/contest';
import { HomeContext } from './home.context';
import {
	AuthenticationDialog,
	ModeType as AuthenticationType,
} from '@features/direct-login';

import { useInstallAction } from '@features/install-pwa';
import { LogOutDialog } from '@features/logout-confirm';
import { EyeIndicator } from '@pages/drafts';
import { useMode, type HomeSearch } from './home.hooks';
import { useHomeDrawers } from '@shared/lib/home-drawer-route';
import { AlignLeft, ArrowLeft, BookOpen, Globe } from 'lucide-react';

import { clearPersistStorage } from '@shared/query-client/query-client';
import { useT } from '@shared/i18n';


export const HomeView = () => {
	const [selectedIssue, setSelectedIssue] = useState<Issue | null>(null);
	const mode = useMode();
	const [isAuthenticated, setIsAuthenticated] = useState(
		config.app.auth.enabled ? !!localStorage.getItem(AUTH_KEY) : true,
	);
	const [showLogout, setShowLogout] = useState(false);
	const [isAppMenuOpen, setIsAppMenuOpen] = useState(false);
	const {
		isFavoritesOpen,
		setIsFavoritesOpen,
		isDownloadsOpen,
		setIsDownloadsOpen,
	} = useHomeDrawers();
	const install = useInstallAction();

	useEffect(() => {
		if (!localStorage.getItem(FAVORITE_KEY)) {
			localStorage.setItem(FAVORITE_KEY, '[]');
		}
	}, []);

	// Controls Authentication
	const [isAuthOpen, setAuthIsOpen] = useState(true);
	const [authMode, setAuthMode] = useState<AuthenticationType>('login');
	const onSuccess = () => {
		// Only discard downloaded content when the *reader* actually changed.
		// This used to wipe unconditionally, which is right when a different
		// person signs in on this device (nobody may be served another
		// reader's downloads) but wrong for the same reader signing in again
		// -- the common case -- where it threw away every issue they had
		// downloaded for offline reading, for nothing. `hasCacheOwnerChanged`
		// answers "someone else?" and errs towards `true` whenever it can't
		// tell, so an unidentifiable session behaves exactly as before.
		// Awaited before anything can refetch, which the previous version did
		// not do: `clearPersistStorage()` is async and walks a *snapshot* of
		// the stored keys, removing them one at a time, while
		// `invalidateQueries` below immediately sets a refetch going. Started
		// without awaiting, the wipe could therefore delete the entry that
		// refetch had just persisted -- leaving the reader signed in with
		// nothing cached, and no indication anything had gone wrong.
		void (async () => {
			if (hasCacheOwnerChanged()) {
				await clearPersistStorage();
				defaultQueryClient.clear();
			}
			rememberCacheOwner();
			// Unconditional, unlike the wipe above: this only marks the list
			// stale so it refetches, which is worth doing on any sign-in
			// since entitlements may have changed. It doesn't discard
			// anything.
			await defaultQueryClient.invalidateQueries({
				queryKey: ['issues'],
			});
		})();
		// Left outside the async block: the reader should be through the
		// login screen the moment they authenticate, not held there while the
		// cache is tidied behind them.
		localStorage.setItem(AUTH_KEY, 'true');
		setIsAuthenticated(true);
		setAuthIsOpen(false);
	};
	const onClickLogOut = useCallback(() => {
		// Still unconditional here, and deliberately so: signing out is the
		// point at which this device stops belonging to this reader, so the
		// downloads go with the session. `forgetCacheOwner` drops the
		// recorded owner alongside it, so the next sign-in doesn't match
		// against a cache that no longer exists.
		forgetCacheOwner();
		// Same ordering hazard as `onSuccess` above: the wipe is async and
		// `invalidateQueries` starts a refetch, so awaiting it first keeps
		// the wipe from deleting whatever that refetch persists behind it.
		void (async () => {
			await clearPersistStorage();
			defaultQueryClient.clear();
			await defaultQueryClient.invalidateQueries({
				queryKey: ['issues'],
			});
		})();
		if (localStorage.getItem(AUTH_KEY)) {
			localStorage.removeItem(AUTH_KEY);
		}
		if (localStorage.getItem(ACCESS_TOKEN_KEY)) {
			localStorage.removeItem(ACCESS_TOKEN_KEY);
		}

		if (localStorage.getItem(ACCESS_TOKEN_EXPIRE_AT_KEY)) {
			localStorage.removeItem(ACCESS_TOKEN_EXPIRE_AT_KEY);
		}
		if (config.app.auth.type === 'token') {
			if (localStorage.getItem(AUTH_EXPIRATION_DATE_KEY)) {
				localStorage.removeItem(AUTH_EXPIRATION_DATE_KEY);
			}
		}

		if (localStorage.getItem(USER_ROLE)) {
			localStorage.removeItem(USER_ROLE);
		}

		if (localStorage.getItem(FAVORITE_KEY)) {
			localStorage.setItem(FAVORITE_KEY, '[]');
		}

		setIsAuthenticated(false);
		// window.location.href = '/';
	}, []);

	const sectionItems = useSectionItems(mode);

	// Built above the two early returns below (the unauthenticated view and
	// the `!mode` bail-out) -- hooks can't be called after a conditional
	// return. `mode` is optional on HomeContextProps, so no narrowing is
	// lost by moving it up here.
	const contextValue = useMemo(
		() => ({
			mode,
			isAuthenticated,
			selectedIssue,
			setSelectedIssue,
			setAuthIsOpen,
			onClickLogOut,
			setShowLogout,
			toggleAppMenu: () => setIsAppMenuOpen((open) => !open),
		}),
		[mode, isAuthenticated, selectedIssue, onClickLogOut],
	);

	if (
		!isAuthenticated &&
		config.app.auth.enabled &&
		(config.app.auth.type === 'login' || config.app.auth.type === 'jwt')
	) {
		return <AuthenticationView onSuccess={onSuccess} />;
	}

	const onCloseDialog = () => {
		setAuthIsOpen(false);
	};
	const onClickSignIn = () => {
		setAuthIsOpen(true);
		setAuthMode('login');
	};
	const onClickSignUp = () => {
		setAuthIsOpen(true);
		setAuthMode('signup');
	};

	if (!mode) {
		return null;
	}

	// Only passed while someone is signed in, so the menu's Sign out row
	// shows exactly when the old navbar's Log Out button did.
	const onSignOut =
		config.app.auth.enabled && isAuthenticated
			? () => setShowLogout(true)
			: undefined;

	return (
		<HomeContext.Provider value={contextValue}>
			<LogOutDialog
				show={showLogout}
				setShowLogout={setShowLogout}
				onClickLogOut={onClickLogOut}
			/>
			<div className={styles['home']}>
				<div className={styles['view']}>
					<Topbar />
					<Content />
				</div>
				{/* Was rendered unconditionally with `isOpen={isAuthenticated
				    ? false : isAuthOpen}` -- `isOpen` only toggles a CSS
				    class (`authentication-dialog.tsx`'s `.dialog.open`,
				    `transform: translateY(...)`), it never stops the form
				    from mounting. For poets' actual tenant config (`auth.type:
				    'login'`), the unauthenticated case is handled entirely by
				    this component's own early return above
				    (`AuthenticationView`, its own separate dialog instance)
				    -- once authenticated, `isOpen` here is permanently
				    `false` with nothing left in the authenticated UI that
				    ever calls `onClickSignIn`/`setAuthIsOpen(true)` to open
				    it again, so this copy sat in the DOM forever: visually
				    hidden, but its inputs/buttons stayed in tab order.
				    Tabbing through the page eventually focused them,
				    scrolling an invisible login form into view -- reported
				    as "hitting Tab redirects me to the login screen."
				    Rendering `null` while authenticated removes it from the
				    DOM (and the tab order) entirely rather than just hiding
				    it visually. Still rendered when *not* authenticated:
				    `config.app.auth.type` values other than `login`/`jwt`
				    (e.g. `cognito`) skip the early-return branch above and
				    reach this render path unauthenticated, where this is
				    the real, reachable login surface -- `isAuthOpen`
				    defaults to `true`, so it's open by default for them. */}
				{!isAuthenticated && (
					<AuthenticationDialog
						mode={authMode}
						setMode={setAuthMode}
						onClickSignUp={onClickSignUp}
						onClickLogIn={onClickSignIn}
						onCloseDialog={onCloseDialog}
						isOpen={isAuthOpen}
						onSuccess={onSuccess}
					/>
				)}
			</div>
			<AppMenu
				open={isAppMenuOpen}
				setOpen={setIsAppMenuOpen}
				templateItems={sectionItems}
				onOpenFavorites={() => setIsFavoritesOpen(true)}
				onOpenDownloads={() => setIsDownloadsOpen(true)}
				install={install}
				onSignOut={onSignOut}
			/>
			<FavoritesDrawer
				open={isFavoritesOpen}
				onOpenChange={setIsFavoritesOpen}
			/>
			<DownloadsDrawer
				open={isDownloadsOpen}
				onOpenChange={setIsDownloadsOpen}
			/>
		</HomeContext.Provider>
	);
};

const Topbar = () => {
	const t = useT();
	const { website } = config.build;
	const navigate = useNavigate();

	const { selectedIssue, setSelectedIssue, toggleAppMenu } =
		useContext(HomeContext);
	const navbarIconUrl = useNavbarIconUrl();

	const logo = website ? (
		<a href={website} target="_blank" rel="noreferrer">
			{' '}
			<img src={navbarIconUrl} alt={t('common.logo')} />{' '}
		</a>
	) : (
		<img src={navbarIconUrl} alt={t('common.logo')} />
	);

	if (selectedIssue) {
		return (
			<TopbarWidget
				className={styles['topbar']}
				left={
					// Deliberately not the widget's own `BackButton`: that one
					// calls `useGoBack()`, whereas this has to clear the
					// selected issue as well as navigate, and drop `id` from
					// the search params. Same markup, different behaviour.
					<button
						type="button"
						onClick={() => {
							navigate({
								to: '/',
								search: {
									id: undefined,
								},
							});
							setSelectedIssue(null);
						}}
					>
						<ArrowLeft color="var(--cf-topbar-stroke)" />
					</button>
				}
				center={<div className={styles['title']}>{logo}</div>}
			/>
		);
	}

	// Same shape as basic's home: every action lives in the AppMenu.
	return (
		<TopbarWidget
			className={styles['topbar']}
			left={<EyeIndicator />}
			center={<div className={styles['title']}>{logo}</div>}
			right={<AppMenuButton onClick={() => toggleAppMenu?.()} />}
		/>
	);
};

function Content() {
	const { mode } = useContext(HomeContext);
	let component = null;
	switch (mode) {
		case 'daily-news':
			component = <DailyNews />;
			break;
		case 'top-stories':
			component = <TopStories />;
			break;
		case 'contest':
			component = <Contest />;
			break;
		default:
			component = <HomeMagazine />;
			break;
	}
	return (
		<div id={MAIN_CONTENT_ID} tabIndex={-1} className={styles['content']}>
			{component}
		</div>
	);
}

function HomeMagazine() {
	const navigate = useNavigate();
	useEffect(() => {
		// Keeps the rest of the search (`dialog` in particular), so landing
		// on a bare `/?dialog=favorites` doesn't drop the drawer from the URL.
		navigate({
			to: '/',
			search: (prev: HomeSearch) => ({
				...prev,
				section: 'magazines',
			}),
			replace: true,
		});
	}, [navigate]);

	return <Magazines />;
}

/**
 * The feed sections, as rows at the top of the AppMenu. Only for a tenant
 * with `feed` on: without it Magazine is the only section, so there's
 * nothing to switch between.
 */
function useSectionItems(
	mode: ReturnType<typeof useMode>,
): Array<AppMenuTemplateItem> | undefined {
	const navigate = useNavigate();
	const template = config.app.template as TemplatePoets;
	const { feed = false } = template.config ?? {};
	if (!feed) {
		return undefined;
	}
	const go = (section: 'top-stories' | 'daily-news' | 'magazines') => {
		void navigate({ to: '/', search: { section } });
	};
	return [
		{
			id: 'top-stories',
			label: 'Latest',
			icon: <AlignLeft />,
			active: mode === 'top-stories',
			onSelect: () => go('top-stories'),
		},
		{
			id: 'daily-news',
			label: 'Daily News',
			icon: <Globe />,
			active: mode === 'daily-news',
			onSelect: () => go('daily-news'),
		},
		{
			id: 'magazines',
			label: 'Magazine',
			icon: <BookOpen />,
			active: mode === 'magazines',
			onSelect: () => go('magazines'),
		},
	];
}

export default HomeView;
